import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const elementFactoryCode = `// 1. The document.createElement Factory
const container = document.createElement('section');
container.id = 'feature-container';
container.className = 'grid-layout';

// 2. Creating text nodes explicitly (useful for mixed content)
const heading = document.createElement('h2');
const textPart = document.createTextNode('Dynamic Feature: ');
const strongPart = document.createElement('strong');
strongPart.textContent = 'Hardware Acceleration';

heading.append(textPart, strongPart); // Modern variadic append
container.appendChild(heading);

// 3. Namespace-aware creation (for SVG/MathML)
const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
svg.setAttribute('viewBox', '0 0 100 100');`;

const insertionPatternsCode = `// Modern variadic insertion methods (IE11 not supported)

const parent = document.querySelector('main');
const firstChild = parent.firstElementChild;

// 1. Prepend - Insert at the very start of the parent
parent.prepend(document.createElement('nav'));

// 2. Append - Multiple arguments support!
const div1 = document.createElement('div');
const div2 = document.createElement('div');
parent.append(div1, div2, "End-of-content flag");

// 3. Sibling insertion relative to an existing anchor
firstChild.before(document.createElement('header'));
firstChild.after(document.createElement('hr'));

// 4. Overwriting children efficiently
parent.replaceChildren(div1, "I have replaced everything else.");`;

const fragmentBufferCode = `// Architectural Performance: The Off-Screen Buffer

function renderMassiveList(items) {
    const list = document.getElementById('item-list');
    
    // Create an invisible DocumentFragment. 
    // It exists only in memory and has NO parent in the DOM tree.
    const fragment = document.createDocumentFragment();

    items.forEach(data => {
        const li = document.createElement('li');
        li.className = 'list-item';
        li.textContent = data.label;
        
        // Appending to a fragment does NOT trigger a reflow.
        fragment.appendChild(li); 
    });

    // Inverting the fragment into the live DOM tree.
    // This triggers exactly ONE reflow for the entire operation.
    list.append(fragment);
    
    // Crucial: The fragment is emptied automatically after insertion.
    console.log(fragment.childNodes.length); // 0
}`;

const templateCloningCode = `// High-Performance Reusable Templates

/**
 * \u003ctemplate id="user-template"\u003e
 *   \u003cdiv class="user-pill"\u003e
 *     \u003cspan class="user-name"\u003e\u003c/span\u003e
 *   \u003c/div\u003e
 * \u003c/template\u003e
 */

function createPill(name) {
    const template = document.getElementById('user-template');
    
    // cloneNode(true) creates a DEEP copy, including all children.
    // The content of a <template> is a DocumentFragment!
    const clone = template.content.cloneNode(true);
    
    // Slicing into the fragment to apply data
    clone.querySelector('.user-name').textContent = name;
    
    return clone; // Returning the fragment for Batch insertion
}`;

const builderPatternCode = `// Industrial Pattern: The DOM Builder Class

class ElementBuilder {
    constructor(tag) {
        this.el = document.createElement(tag);
    }
    
    as(className) {
        this.el.className = className;
        return this;
    }
    
    withText(text) {
        this.el.textContent = text;
        return this;
    }
    
    nested(...children) {
        this.el.append(...children.map(c => c instanceof ElementBuilder ? c.build() : c));
        return this;
    }
    
    build() {
        return this.el;
    }
}

// Fluent API Usage
const card = new ElementBuilder('div').as('card')
    .nested(
        new ElementBuilder('h3').withText('Modular Build'),
        new ElementBuilder('p').withText('Pattern: Builder for DOM').build()
    ).build();`;

const JSDOMCreation = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">DOM Manipulation</div>
                <h1>Architectural Element Creation</h1>
                <p className="hero-lead">
                    Go beyond simple scripting. Master the internal factory methods for
                    node generation, implement off-screen document buffers with
                    `DocumentFragment`, and build reusable UI components using the
                    native <code>&lt;template&gt;</code> element for maximum performance.
                </p>
            </div>

            <div className="content-section">
                <h2>The Factory Pattern: document.createElement</h2>
                <p>
                    In the browser environment, new elements are not created with the `new` keyword,
                    but through the `document.createElement()` factory. This method serves as the
                    entry point to the browser's C++ allocation engine, reserving memory for a new
                    node archetype and initializing it with default styles and properties. At this
                    stage, the element is "detached"it exists in the JavaScript memory space but
                    is not part of the active render tree. This detached state is an architectural
                    advantage, as you can modify its classes, styles, and attributes without
                    triggering any visual updates or expensive reflows. Junior developers often
                    append an element first and then modify it; senior engineers perform all
                    configuration while the element is detached to ensure the browser only
                    has to calculate its layout once.
                </p>
                <p>
                    Beyond standard HTML tags, advanced applications may require specialized
                    element creation. The `createElementNS` method allows for the creation of
                    elements within specific XML namespaces, which is essential for working with
                    SVG (Scalable Vector Graphics) or MathML. Without the correct namespace, browsers
                    will treat an <code>&lt;svg&gt;</code> tag as a custom HTML element rather than a graphic
                    container, leading to rendering failures. Additionally, for complex text
                    scenariossuch as building a rich-text editoryou should use
                    `document.createTextNode()` to create granular text entities that can be
                    inserted between other tags. This technical precision ensures that your
                    application maintains a clean, standards-compliant DOM structure and
                    operates with surgical accuracy across different browser engines.
                </p>

                <CodeBlock code={elementFactoryCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Modern Insertion: Bypassing Legacy API</h2>
                <p>
                    For over a decade, the DOM API was limited to `appendChild` and `insertBefore`,
                    which were often cumbersome and required manual text-node creation. Modern
                    JavaScript introduces a highly intuitive suite of insertion methods: `append()`,
                    `prepend()`, `before()`, and `after()`. These methods are "variadic," meaning
                    they can accept multiple arguments at onceincluding both Element nodes and
                    raw strings. When a string is passed, the browser automatically converts it
                    into a `TextNode`, drastically simplifying the code needed to build dynamic
                    messages. Furthermore, the `replaceChildren()` method provides an
                    unmatched performance boost for updating containers; it clears all old
                    children and inserts new ones in a single synchronized operation,
                    preventing the flickering associated with manual loops.
                </p>
                <p>
                    When choosing an insertion method, consider the architectural impact on
                    document flow. `prepend()` is frequently used for feed-style interfaces
                    where the newest items appear at the top, while `after()` is ideal for
                    contextual UIs like error messages appearing immediately below an input field.
                    It is also worth noting that these modern methods do not return the inserted
                    node (they return `undefined`), unlike the legacy `appendChild`. This shift
                    toward a more declarative, "fire-and-forget" style align with modern component
                    based architectures. By prioritizing these APIs, you write more expressive
                    code that communicates its structural intent clearly while reducing the
                    boilerplate required to manage the parent-child relationships of
                    the tree.
                </p>

                <CodeBlock code={insertionPatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>DocumentFragments: The Off-Screen Buffer</h2>
                <p>
                    One of the most powerful tools in the DOM API is the `DocumentFragment`.
                    Architecturally, a fragment is a "lightweight" version of a document;
                    it has no parent and does not exist in the DOM tree. When you append
                    an element to a fragment, you are effectively working in a private,
                    off-screen memory buffer. Because the fragment is not part of the active
                    page, modifications made to it do **not** trigger any layout calculations,
                    styles recalculations, or repaints. This makes it the perfect vehicle
                    for heavy batch operations. If you need to render 1,000 list items from
                    an API response, appending them to a fragment first ensures that the
                    user's browser only performs the expensive "reflow" calculation once,
                    rather than 1,000 times.
                </p>
                <p>
                    A unique property of the `DocumentFragment` is its behavior during insertion.
                    When you call `parent.append(fragment)`, it is not the fragment itself that
                    is inserted, but all of the fragment's children. After the operation is
                    complete, the fragment is automatically emptied and can be reused for the
                    next batch of operations. This "teleportation" mechanism is highly efficient
                    and avoids the memory overhead of maintaining the fragment container within
                    the live DOM. Every industrial-grade JS framework, from React to Vue, uses
                    internal fragments to manage DOM updates efficiently. Mastering this
                    low-level buffer technique is foundational for building high-performance
                    dashboards and data-heavy applications that stay responsive under
                    significant load.
                </p>

                <CodeBlock code={fragmentBufferCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Templates and Cloning: Blueprints for Performance</h2>
                <p>
                    The <code>&lt;template&gt;</code> element is a unique HTML architectural component designed
                    for reusability. Unlike a <code>&lt;div&gt;</code> with `display: none`, the content of a
                    template is not processed by the browser until it is explicitly cloned.
                    Images don't download, scripts don't run, and styles aren't applied. This
                    makes templates the ultimate "blueprint" for components. In your JavaScript,
                    you access the template's `.content` property (which is a DocumentFragment)
                    and use `.cloneNode(true)` to create a precise duplicate of the structure.
                    This approach is significantly faster than building elements manually from
                    scratch using `createElement` calls, as the browser's engine can
                    copy internal memory blocks rather than executing individual
                    instructions.
                </p>
                <p>
                    Cloning is particularly effective for repeated structures like "User Cards"
                    or "Product Items." By defining the structure in HTML and only filling in
                    the data via JavaScript, you maintain a healthy separation of concerns while
                    leveraging the browser's native cloning optimizations. When performing a
                    deep clone (`true`), ensure you update IDs if necessary, as duplicate IDs
                    can break accessibility and form labels. Senior engineers often wrap
                    cloning logic into "Factory Functions" or Classes, returning the ready-to-use
                    fragment to the main logic. This pattern minimizes the "string
                    manipulation" often found in legacy code and creates a bridge toward
                    the component-based paradigms found in modern frameworks.
                </p>

                <CodeBlock code={templateCloningCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Patterns for Scale: Builders and Factories</h2>
                <p>
                    As your application grows, manually managing hundreds of `createElement`
                    calls becomes unmaintainable. The solution is the **Builder Pattern** or
                    **Factory Pattern**. A Builder class allows for a "fluent" API where you
                    chain calls like `.as('card').withText('Hello')` to configure an element
                    before final construction. This makes the code read like a configuration
                    file rather than a series of imperative steps. A Factory function, on the
                    other hand, acts as a single-entry point that abstracts the complexity
                    of node creation, property assignment, and event registration into a
                    reusable utility. These abstractions are the building blocks of
                    modern design systems and contribute to a codebase that is both
                    testable and architecturally coherent.
                </p>

                <CodeBlock code={builderPatternCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Separation:** Configure elements while they are "detached" from the DOM.</li>
                    <li>**Modernity:** Favor `append()` and `prepend()` over legacy `appendChild`.</li>
                    <li>**Scale:** Use `DocumentFragment` as an off-screen buffer for all batch operations.</li>
                    <li>**Reusability:** Master the <code>&lt;template&gt;</code> element for high-performance cloning.</li>
                    <li>**Precision:** Use `createElementNS` for SVG and non-HTML namespace elements.</li>
                    <li>**Abstraction:** Wrap complex creation logic in Factory or Builder patterns.</li>
                    <li>**Performance:** Batch injections to trigger exactly ONE reflow cycle.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDOMCreation;

